/**
 * Dialog and button for bulk-generating new SIM levels from an existing level template.
 *
 * @module components/sim/createLevels/GenerateSim
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    TextField,
    Tooltip,
} from '@material-ui/core';
import {useLocation} from 'react-router-dom';
import {useStyle,} from './CreateLevels';
import {fetchSimDetails, generateSim,} from '../../../redux/sims/actions';

/**
 * A custom hook that creates and returns an instance of `URLSearchParams`
 * based on the current location's search string.
 *
 * This hook is typically used in React applications to parse query parameters
 * from the URL. It utilizes `useLocation` to access the current location object
 * and extracts the search string, which is then converted into a
 * `URLSearchParams` instance for convenient query parameter manipulation.
 *
 * @function
 * @returns {URLSearchParams} An instance of `URLSearchParams` representing the query
 * parameters in the current URL.
 */
const useQuery = () => new URLSearchParams(useLocation().search);

/**
 * The `GenerateSim` component is responsible for managing the process of generating simulation levels
 * dynamically based on user inputs and predefined configurations. It provides functionalities for:
 * - Handling level generation with customizable attributes.
 * - Managing state updates related to the creation and manipulation of simulation levels.
 * - Interfacing with user actions such as toggling checkboxes, modifying level properties, and cancelling operations.
 *
 * The component uses various hooks such as `useState`, `useEffect`, and `useQuery` to handle state management,
 * side effects, and query-related functionalities respectively. It interacts with parent-level state setters
 * and other callback functions to ensure smooth operations of the simulation generation workflow.
 *
 * Properties:
 * @param {Object} newSim - Contains the data structure for the newly created simulation.
 * @param {number} currentIndex - Represents the current level index being worked on.
 * @param {Object} data - The state object containing information about all existing levels.
 * @param {Array} tabs - Represents the tabs or configuration options for level generation.
 * @param {Function} setTotalLevel - A state setter function to update the total number of levels.
 * @param {number} totalLevel - The current count of total levels in the simulation.
 * @param {Function} setSimGenerated - A state setter function to indicate whether a simulation has been generated.
 * @param {Function} setLevelSelect - A state setter to control the visibility of the level selection UI.
 * @param {boolean} levelSelect - A flag indicating whether the level selection UI is active.
 * @param {Function} handleClose - A callback function to handle dialog close operations.
 * @param {Function} setLoad - A state setter function to trigger the loading state during asynchronous operations.
 * @param {Function} setFlag - A state setter to indicate the status of processes during generation.
 * @param {Function} generateSim - A callback function to trigger the simulation generation process.
 * @param {Function} setAlertType - A state setter function to define the type of feedback alert.
 * @param {Function} setAlertMessage - A state setter to define the message content for feedback alerts.
 * @param {Function} setOpenAlert - A state setter to control the visibility of feedback alerts.
 */
const GenerateSim = ({
    newSim,
    currentIndex,
    data,
    tabs,
    setTotalLevel,
    totalLevel,
    setSimGenerated,
    setLevelSelect,
    levelSelect,
    handleClose,
    setLoad,
    setFlag,
    generateSim,
    setAlertType,
    setAlertMessage,
    setOpenAlert,
}) => {
    const classes = useStyle();
    const [disableButton, setGenerateSim] = useState(true);
    const [newLevels, setNewLevels] = React.useState('');
    const [defLevels] = React.useState([
        'Watch and Learn',
        'Watch the experts',
        'Try w/ scripts',
        'Try w/ keywords',
        'Try w/o help',
    ]);
    const [generateClicked, setGenerateClicked] = useState(false);
    /**
     * Represents the result of executing a query using the `useQuery` hook.
     *
     * The `query` variable is used to fetch and manage data from a specified source,
     * typically in the context of a GraphQL API or similar data-fetching mechanism.
     * It provides state and utility functions for handling the query lifecycle,
     * such as loading, error handling, and access to the resolved data.
     *
     * This variable is typically used in functional components to retrieve and render
     * data dynamically while leveraging the reactivity of a state management system.
     */
    const query = useQuery();
    /**
     * Toggles the state for generating simulations.
     *
     * The `showGenerateSim` function is responsible for disabling the simulation
     * generation process by updating the relevant state. When invoked, it sets
     * the `generateSim` state to `false`, effectively turning off the functionality
     * related to creating or generating simulations.
     */
    const showGenerateSim = () => {
        setGenerateSim(false);
    };

    useEffect(() => {
        if (
            newSim?.levels[currentIndex]
            && newSim?.levels?.[currentIndex]?.scripts?.length >= 1
        ) {
            showGenerateSim();
        }
    });

    /**
     * A function that prepares and enables the generation of levels in a simulation.
     * It processes simulation characters, generates level configuration objects,
     * and updates related states to reflect the newly generated levels.
     *
     * Functionality:
     * - Identifies and assigns agent and coach identifiers from simulation characters.
     * - Iterates to generate four levels with specific configurations and properties.
     * - Configures properties like level order, inclusion of keywords, autopopulation,
     *   and agent or coach removals based on level index.
     * - Updates the total number of levels, stores the generated levels, and sets the
     *   simulation and level selection states.
     */
    const enableIcon = () => {
        let coachid;
        let agentid;
        setGenerateClicked(false);
        newSim.sim_characters.forEach((char) => {
            if (char.type === 'agent') {
                agentid = char._id;
            }
            if (char.type === 'coach') {
                coachid = char._id;
            }
        });
        const {length} = data.levels;
        const genLevels = [];
        for (let i = 0; i < 4; i += 1) {
            const obj = {};
            obj.name = defLevels[i + 1];
            obj.order = data.levels[length - 1].order + i + 1;
            obj.sim_id = newSim.id;
            obj.hint_text = false;
            obj.keyword_score = false;
            obj.coachId = coachid;
            obj.agentId = agentid;
            obj.scripts = [];
            obj.generateLevel = true;
            if (i === 0) {
                obj.removecoach = true;
                obj.includekeywords = false;
                obj.hinttext = false;
                obj.removeagent = false;
                obj.convertagent = false;
                obj.autopopulate = true;
                obj.autopopulate_data = tabs;
            }
            if (i === 1) {
                obj.removecoach = false;
                obj.includekeywords = false;
                obj.hinttext = false;
                obj.removeagent = false;
                obj.convertagent = true;
                obj.autopopulate = false;
                obj.autopopulate_data = tabs;
            }
            if (i === 2) {
                obj.removecoach = false;
                obj.includekeywords = true;
                obj.removeagent = false;
                obj.hinttext = false;
                obj.convertagent = true;
                obj.autopopulate = false;
                obj.autopopulate_data = tabs;
            }
            if (i === 3) {
                obj.removeagent = true;
                obj.includekeywords = false;
                obj.hinttext = true;
                obj.removecoach = true;
                obj.convertagent = true;
                obj.autopopulate = false;
                obj.autopopulate_data = tabs;
            }
            genLevels.push(obj);
        }
        setTotalLevel(totalLevel + genLevels.length);
        setNewLevels(genLevels);
        setSimGenerated(true);
        setLevelSelect(true);
    };

    /**
     * Updates the name of a level in the `newLevels` array based on user input.
     *
     * @param {Object} event - The event object containing the input value.
     * @param {number} index - The index of the level in the `newLevels` array to be updated.
     */
    const handleLevelName = (event, index) => {
        const arr = [...newLevels];
        arr[index].name = event.target.value;
        setNewLevels(arr);
    };

    /**
     * Handles the checkbox state change for generating levels.
     *
     * This function is triggered when a checkbox is toggled. It updates the
     * `generateLevel` property of the corresponding level in the `newLevels` array
     * based on the checkbox state. Additionally, it adjusts the `totalLevel`
     * count based on whether the checkbox has been checked or unchecked.
     *
     * @param {Object} event - The event object triggered by the checkbox toggle.
     * @param {number} index - The index of the level being updated in the `newLevels` array.
     */
    const handleGenerateCheckbox = (event, index) => {
        const arr = [...newLevels];
        if (event.target.checked === true) {
            arr[index].generateLevel = true;
            setTotalLevel(totalLevel + 1);
        } else {
            setTotalLevel(totalLevel - 1);
            arr[index].generateLevel = false;
        }
        setNewLevels(arr);
    };

    /**
     * Handles the change event for a checkbox related to the "removecoach" property in a specific index of the `newLevels` array.
     *
     * @param {Object} event - The event object generated from the checkbox interaction.
     * @param {boolean} event.target.checked - The current checked state of the checkbox.
     * @param {number} index - The index of the item in the `newLevels` array to be updated.
     */
    const handleRemCoachCheckbox = (event, index) => {
        const arr = [...newLevels];
        switch (event.target.checked) {
            case true:
                arr[index].removecoach = true;
                setNewLevels(arr);
                return;

            case false:
                arr[index].removecoach = event.target.checked;
                setNewLevels(arr);
                return;
            default:
                setNewLevels(newLevels);
        }
    };

    /**
     * Handles the checkbox state change for including keywords in a specific item.
     *
     * Updates the `includekeywords` property and `scripts` array of an item
     * at the specified index within the `newLevels` array based on the checkbox's
     * checked state. Updates the state of `newLevels` accordingly.
     *
     * @param {Object} event - The event object representing the change event of the checkbox.
     * @param {boolean} event.target.checked - Indicates whether the checkbox is checked or unchecked.
     * @param {number} index - The index of the item in the `newLevels` array to update.
     */
    const handleKeywordsCheckbox = (event, index) => {
        const arr = [...newLevels];
        switch (event.target.checked) {
            case true:
                arr[index].scripts = [];
                arr[index].includekeywords = true;
                setNewLevels(arr);
                return;

            case false:
                arr[index].includekeywords = false;
                arr[index].scripts = [];
                setNewLevels(arr);
                return;

            default:
                setNewLevels(arr);
        }
    };

    /**
     * Handles the `onChange` event for a checkbox that toggles the removal status of an agent.
     *
     * @param {Object} event - The event object triggered by the checkbox change.
     * @param {boolean} event.target.checked - Indicates whether the checkbox is checked or not.
     * @param {number} index - The index of the level in the `newLevels` array to be updated.
     */
    const handleRemAgentCheckbox = (event, index) => {
        const arr = [...newLevels];
        switch (event.target.checked) {
            case true:
                arr[index].removeagent = true;
                setNewLevels(arr);
                return;
            case false:
                arr[index].removeagent = event.target.checked;
                setNewLevels(arr);
                return;
            default:
                setNewLevels(arr);
        }
    };

    /**
     * Handles the checkbox selection event for updating the state of a specific item in a list.
     *
     * @param {Object} event - The event object triggered by the checkbox interaction.
     * @param {number} index - The index of the item in the list to be updated.
     *
     * Updates the `convertagent` property of the item at the specified index in the `newLevels` array
     * based on the state of the checkbox (`event.target.checked`). The updated array is then passed
     * to the `setNewLevels` function to update the state.
     */
    const handleCheckboxSelect = (event, index) => {
        const arr = [...newLevels];
        switch (event.target.checked) {
            case true:
                arr[index].convertagent = true;
                setNewLevels(arr);
                return;
            case false:
                arr[index].convertagent = false;
                setNewLevels(arr);
                return;
            default:
                setNewLevels(arr);
        }
    };

    /**
     * Handles the state of the "hint text" checkbox for a specific level and updates the associated data.
     *
     * @param {Object} event - The event object triggered by the checkbox interaction.
     * @param {number} index - The index of the level being modified within the `newLevels` array.
     *
     * The function modifies the `hinttext` property of the selected level and updates the hint text
     * for each script of that level based on the checkbox state (checked or unchecked). If checked,
     * the hint texts are populated from `data.levels`. If unchecked, the hint texts are cleared.
     * The `newLevels` state is updated accordingly after modifying the data.
     */
    const handleHintTextCheckbox = (event, index) => {
        const arr = [...newLevels];
        const levels = data.levels.slice(0, 1);
        switch (event.target.checked) {
            case true:
                arr[index].hinttext = true;
                for (let i = 0; i < arr[index].scripts.length; i += 1) {
                    arr[index].scripts[i].hint_text = levels[0].scripts[i].hint_text;
                }
                setNewLevels(arr);
                return;
            case false:
                arr[index].hinttext = false;
                for (let i = 0; i < arr[index].scripts.length; i += 1) {
                    arr[index].scripts[i].hint_text = '';
                }
                setNewLevels(arr);
                return;
            default:
                setNewLevels(arr);
        }
    };

    /**
     * Handles the state update for the autopopulate checkbox in a given array of levels.
     *
     * @param {Object} event - The event object triggered by the checkbox input.
     * @param {number} index - The index of the level in the array to be updated.
     */
    const handleAutopopulateCheckbox = (event, index) => {
        const arr = [...newLevels];
        switch (event.target.checked) {
            case true:
                arr[index].autopopulate = true;
                setNewLevels(arr);
                return;
            case false:
                arr[index].autopopulate = false;
                setNewLevels(arr);
                return;
            default:
                setNewLevels(arr);
        }
    };

    /**
     * Handles the cancel action for a specific operation.
     *
     * Sets the level selection state to false and resets the total levels
     * to the current length of the levels data array.
     *
     * @function
     */
    const handleCancel = () => {
        setLevelSelect(false);
        setTotalLevel(data.levels.length);
    };

    /**
     * Handles the generation of simulation levels based on the provided configuration and updates the application state.
     *
     * This asynchronous function performs the following tasks:
     * - Sets the loading indicator to true.
     * - Filters out levels from the `newLevels` array where the `generateLevel` property is set to `false`.
     * - Removes the `generateLevel` property from the remaining levels.
     * - Sets internal states to indicate the generation process has been started.
     * - Calls the `generateSim` function to create simulation levels on the server.
     * - Fetches the updated simulation details using `fetchSimDetails`.
     * - Resets specific states and displays a success alert.
     * - Automatically closes the alert after a specified duration.
     *
     * Note:
     * - Contains a known issue where generating levels with a first level containing only one coach script causes issues with generated levels that have the "Remove coach" option enabled.
     *
     * State Updates:
     * - `setLoad` controls the loading spinner visibility.
     * - `setGenerateClicked` tracks if the generation button has been clicked.
     * - `setLevelSelect` manages level selection state.
     * - `setFlag` updates internal logic control.
     * - `setNewLevels` clears the levels array after processing.
     * - `setAlertType`, `setAlertMessage`, and `setOpenAlert` manage alert notifications.
     *
     * Dependencies:
     * - `generateSim(arr, levelId)` is used to send processed levels for simulation.
     * - `fetchSimDetails(simId)` retrieves updated simulation details from the server.
     *
     * Alerts:
     * - Displays a success message upon successful generation of simulation levels.
     */
    const handleDialogGenerate = async () => {
        setLoad(true);
        const arr = [...newLevels];
        for (let i = arr.length - 1; i >= 0; i -= 1) {
            if (arr[i].generateLevel === false) {
                arr.splice(i, 1);
            }
        }
        for (let i = 0; i < arr.length; i += 1) {
            delete arr[i].generateLevel;
        }
        /*
            FIXME - Having a first level with only one coach script will cause
                issues with the generated levels that have enabled the
                "Remove coach" option
         */
        setGenerateClicked(true);
        setLevelSelect(false);
        setFlag(true);
        await generateSim(arr, data.levels[0]._id);
        setNewLevels('');
        const simId = query.get('simId') ? query.get('simId') : data.id;
        await fetchSimDetails(simId);
        setLoad(false);
        setAlertType('success');
        setAlertMessage('Sim levels generated successfully!!!');
        setOpenAlert(true);
        setTimeout(() => setOpenAlert(false), 3000);
    };

    return (
        <>
            <Button
                disabled={disableButton}
                variant="outlined"
                className={classes.generateSimBtn}
                onClick={enableIcon}
            >
                GENERATE SIM
            </Button>
            <Dialog
                open={levelSelect}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
                fullWidth
                maxWidth="md"
            >
                <DialogTitle id="form-dialog-title">Generate Sim</DialogTitle>
                <DialogContent>
                    {newLevels
                        && newLevels.map((level, index) => (
                            <FormControl
                                key={index}
                                component="fieldset"
                                className={classes.dialogFormControl}
                            >
                                <TextField
                                    fullWidth
                                    label="Title"
                                    InputProps={{
                                        classes: {
                                            input: classes.resize,
                                        },
                                    }}
                                    value={level.name}
                                    variant="outlined"
                                    onChange={(event) => handleLevelName(event, index)}
                                />
                                <FormGroup>
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                onChange={(event) => handleGenerateCheckbox(event, index)}
                                                name="generate"
                                                color="primary"
                                                checked={level.generateLevel}
                                            />
                                        )}
                                        label="Generate this level"
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                onChange={(event) => handleRemCoachCheckbox(event, index)}
                                                name="removecoach"
                                                color="primary"
                                                checked={level.removecoach === true}
                                            />
                                        )}
                                        label="Remove coach"
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                onChange={(event) => handleKeywordsCheckbox(event, index)}
                                                name="keywords"
                                                color="primary"
                                                checked={level.includekeywords === true}
                                            />
                                        )}
                                        label="Only include keywords"
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                onChange={(event) => handleRemAgentCheckbox(event, index)}
                                                name="removeagent"
                                                color="primary"
                                                checked={level.removeagent === true}
                                            />
                                        )}
                                        label="Remove Agent Script"
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                onChange={(event) => handleCheckboxSelect(event, index)}
                                                name="convertagent"
                                                color="primary"
                                                checked={level.convertagent === true}
                                            />
                                        )}
                                        label="Convert Agent Clips to Prompts"
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                onChange={(event) => handleHintTextCheckbox(event, index)}
                                                name="hinttext"
                                                color="primary"
                                                checked={level.hinttext === true}
                                            />
                                        )}
                                        label="Include Hint Text"
                                    />
                                    {data.auto_populate ? (
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    onChange={(event) => handleAutopopulateCheckbox(event, index)}
                                                    name="autopopulate"
                                                    color="primary"
                                                    checked={level.autopopulate === true}
                                                />
                                            )}
                                            label="Include Auto-populate"
                                        />
                                    ) : null}
                                </FormGroup>
                            </FormControl>
                        ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} className={classes.cancelButton}>
                        Cancel
                    </Button>
                    <Tooltip
                        title={totalLevel > 5
                            ? 'More than 5 levels are not allowed for a sim.'
                            : ''}
                        enterDelay={500}
                        leaveDelay={200}
                    >
            <span>
              <Button
                  color="primary"
                  className={classes.generateButton}
                  onClick={handleDialogGenerate}
                  disabled={totalLevel > 5 || generateClicked}
              >
                Generate
              </Button>
            </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>
        </>
    );
};

/**
 * PropTypes definition for the GenerateSim component.
 * This defines the expected types and requirements for the props that
 * the GenerateSim component should receive.
 *
 * The GenerateSim component's props are validated against these definitions
 * at runtime. If the prop types do not match these specifications,
 * a warning is issued in the development environment.
 */
GenerateSim.propTypes = {
    newSim: PropTypes.object,
    currentIndex: PropTypes.number,
    data: PropTypes.object,
    tabs: PropTypes.array,
    setTotalLevel: PropTypes.func,
    totalLevel: PropTypes.number,
    setSimGenerated: PropTypes.func,
    setLevelSelect: PropTypes.func,
    levelSelect: PropTypes.bool,
    handleClose: PropTypes.func,
    setLoad: PropTypes.func,
    setFlag: PropTypes.func,
    generateSim: PropTypes.func,
    setAlertType: PropTypes.func,
    setAlertMessage: PropTypes.func,
    setOpenAlert: PropTypes.func,
};

/**
 * Maps the state from the Redux store to the props of a component.
 * This function is typically used to select the necessary parts of the state
 * that a component needs.
 *
 * @param {Object} state - The current state of the Redux store.
 * @returns {Object} An object containing the mapped state properties:
 * - `newSim` refers to the `newSim` property within the `sim` slice of the Redux state.
 * - `tabs` refers to the `autoPopulate` property within the `sim` slice of the Redux state.
 */
const mapStateToProps = (state) => ({
    newSim: state.sim.newSim,
    tabs: state.sim.autoPopulate,
});

/**
 * An object mapping dispatchable actions to props for use in a React-Redux container.
 * This allows components to trigger specific actions within the Redux store.
 *
 * @property {Function} generateSim - A dispatchable action used to initiate or generate a simulation.
 */
const mapDispatchToProps = {
    generateSim,
};

export default connect(mapStateToProps, mapDispatchToProps)(GenerateSim);
